import { hasMarketingConsent } from "./cookieConsent";

const PURCHASE_DEDUP_PREFIX = "aifp_meta_purchase:";
const REGISTRATION_DEDUP_PREFIX = "aifp_meta_registration:";
const PIXEL_ID = "1758458698690516";
const PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

type FbqFn = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: FbqFn;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

export function ensurePixel() {
  if (!hasMarketingConsent()) return;
  if (window.fbq) return;

  const fbq: FbqFn = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFn;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = PIXEL_SCRIPT_SRC;
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  window.fbq("init", PIXEL_ID);
}

function toRonNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Fire Meta Purchase after a payment is confirmed.
 * Isolated from payment flow: never throws, and only once per payment id.
 * Blocked unless the user has given marketing consent.
 */
export function trackPurchase(params: {
  paymentId: string;
  valueRon?: unknown;
}): void {
  try {
    if (!hasMarketingConsent()) return;

    const paymentId = params.paymentId.trim();
    if (!paymentId) return;

    const dedupKey = `${PURCHASE_DEDUP_PREFIX}${paymentId}`;
    if (window.sessionStorage.getItem(dedupKey)) return;

    ensurePixel();

    const payload: { currency: "RON"; value?: number } = { currency: "RON" };
    const valueRon = toRonNumber(params.valueRon);
    if (valueRon !== null) {
      payload.value = valueRon;
    }

    window.fbq?.("track", "Purchase", payload, { eventID: paymentId });
    window.sessionStorage.setItem(dedupKey, "1");
  } catch {
    // Pixel failures must never affect payment confirmation or redirects.
  }
}

/**
 * Fire Meta CompleteRegistration when a new user verifies their email.
 * Isolated from verification flow: never throws, and only once per token.
 * Blocked unless the user has given marketing consent.
 */
export function trackCompleteRegistration(params: {
  token: string;
}): void {
  try {
    if (!hasMarketingConsent()) return;

    const token = params.token.trim();
    if (!token) return;

    const dedupKey = `${REGISTRATION_DEDUP_PREFIX}${token}`;
    if (window.sessionStorage.getItem(dedupKey)) return;

    ensurePixel();

    window.fbq?.(
      "track",
      "CompleteRegistration",
      { content_name: "AIFoiParcurs", status: "activated" },
      { eventID: token },
    );
    window.sessionStorage.setItem(dedupKey, "1");
  } catch {
    // Pixel failures must never affect email verification.
  }
}
